import type { Article, Section } from "@/types/content";
import { newsArticles } from "@/mocks/news";
import { reviewArticles } from "@/mocks/reviews";
import { industryArticles } from "@/mocks/industry";
import { categories } from "@/mocks/categories";

export const allArticles: Article[] = [
  ...newsArticles,
  ...reviewArticles,
  ...industryArticles,
];

export const sectionLabels: Record<Section, string> = {
  news: "新闻",
  review: "评测",
  industry: "行业动态",
};

export const sectionPaths: Record<Section, string> = {
  news: "/news",
  review: "/reviews",
  industry: "/industry",
};

export function getArticleById(id: string): Article | undefined {
  return allArticles.find((item) => item.id === id);
}

export function getArticlesBySection(section: Section): Article[] {
  return allArticles.filter((item) => item.section === section);
}

export function getArticlesByCategory(category: string): Article[] {
  return allArticles.filter((item) => item.category === category);
}

export function getNewsByCategory(category?: string): Article[] {
  const list = newsArticles;
  if (!category) return list;
  return list.filter((item) => item.category === category);
}

export function getReviewsByCategory(category?: string): Article[] {
  const list = reviewArticles;
  if (!category) return list;
  return list.filter((item) => item.category === category);
}

export function getLatest(limit?: number): Article[] {
  const sorted = [...allArticles].sort((a, b) => (a.date < b.date ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getLatestNews(limit?: number): Article[] {
  const sorted = [...newsArticles].sort((a, b) => (a.date < b.date ? 1 : -1));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotReviews(limit = 3): Article[] {
  const sorted = [...reviewArticles].sort((a, b) => b.views - a.views);
  return sorted.slice(0, limit);
}

export function getFeatured(): Article[] {
  const news = getLatestNews(3);
  const review = getHotReviews(2);
  return [...news, ...review].slice(0, 5);
}

export function searchArticles(
  keyword: string,
  type: Section | "all" = "all",
): Article[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allArticles.filter((item) => {
    if (type !== "all" && item.section !== type) return false;
    const haystack = [item.title, item.summary, item.category, item.brand, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRelated(article: Article, limit = 4): Article[] {
  const sameCategory = allArticles.filter(
    (item) =>
      item.id !== article.id &&
      item.category === article.category &&
      item.section === article.section,
  );
  const sameSection = allArticles.filter(
    (item) =>
      item.id !== article.id &&
      item.section === article.section &&
      item.category !== article.category,
  );
  const merged = [...sameCategory, ...sameSection];
  const unique: Article[] = [];
  merged.forEach((item) => {
    if (!unique.some((u) => u.id === item.id)) unique.push(item);
  });
  return unique.slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    page: safePage,
    totalPages,
    total: list.length,
  };
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function formatDate(date: string): string {
  const [y, m, d] = date.split("-");
  return `${y}年${Number(m)}月${Number(d)}日`;
}

export function getCategoryMeta(id: string) {
  return categories.find((item) => item.id === id);
}